import { useCallback, useEffect, useState } from "react";

import Button from "../../../form/Button";
import Select from "../../../form/select/Select";
import Breadcrumb from "../../../common/Breadcrumb";
import Search from "../../../form/Search";
import { useSearchParams } from "react-router-dom";
import MediaCard from "../../../elements/MediaCard";
import { useDebounce } from "../../../../hooks/useDebounce";
import { toast } from "react-toastify";
import { getMediaList } from "../../../../services/mediaService";
import { classNames } from "../../../../helpers/classNames";
import ManageFolder from "../media/ManageFolder";
import UploadFile from "./UploadFile";

export default function AllMediaList({
  onSelect = () => {},
  close = () => {},
  selectedData,
  isSingle = false,
}) {
  const [type, setType] = useState("all");
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);
  const [limit, setLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [list, setList] = useState({
    loading: true,
    data: [],
    pageCount: 0,
    totalItem: 0,
  });
  const [breadcrumbList, setBreadcrumbList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [fileUploadModal, setFileUploadModal] = useState({
    isOpen: false,
    data: null,
  });
  const [pagination, setPagination] = useState(null);

  const folderData = searchParams.get("folderData");

  const optionData = [
    { value: "all", label: "All" },
    { value: "folder", label: "Folders" },
    { value: "image", label: "Images" },
    { value: "video", label: "Videos" },
    { value: "pdf", label: "PDFs" },
    { value: "word", label: "Word Docs" },
    { value: "excel", label: "Excel Sheets" },
    { value: "ppt", label: "Presentations" },
    { value: "csv", label: "CSV Files" },
    { value: "mp3", label: "Audio Files" },
  ];
  function handleNavigate(item) {
    setCurrentPage(null);
    const id = typeof item === "object" ? item?.uid : null;

    if (id) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("folderData", id);
      setSearchParams(updatedParams);
    }
  }

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setList((pre) => ({
        ...pre,
        loading: true,
      }));

      const res = await getMediaList({
        keyword: search,
        limit: limit,
        ...(currentPage && { lastId: currentPage }),
        sortQuery: "-createdAt",
        ...(folderData && folderData !== "root" && { key: folderData }),
      });

      if (res?.success && res?.status === 200) {
        const mediaData = res?.data || [];
        const metadata = res?.nextCursor;

        if (currentPage) {
          setList((prevList) => ({
            ...prevList,
            loading: false,
            data: [...prevList.data, ...mediaData],
            keyword: search,
            nextCursor: metadata,
          }));
        } else {
          setList((prevList) => ({
            // ...prevList,
            loading: false,
            data: mediaData,
            keyword: search,
            nextCursor: metadata,
          }));
        }

        setBreadcrumbList([
          { name: "Home", uid: "root" },
          ...(res?.breadcrumb || []),
        ]);
        setPagination(metadata);
      } else {
        throw new Error(res?.message || "Failed to fetch media list.");
      }
    } catch (error) {
      console.error("Error loading media list:", error);
      toast.error(error.message || "Something went wrong while loading media.");
      setList((pre) => ({ ...pre, data: [], loading: false }));
    } finally {
      setLoading(false);
    }
  }, [limit, currentPage, search, folderData]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const toggleSelectedItem = (item) => {
    const alreadySelected = selectedItems?.some((f) => f._id === item._id);
    let updated = [];

    if (isSingle) {
      updated = alreadySelected ? [] : [item];
    } else {
      updated = alreadySelected
        ? selectedItems.filter((f) => f._id !== item._id)
        : [...selectedItems, item];
    }

    setSelectedItems(updated);
  };

  const isSelected = (file) => {
    return selectedItems?.some((f) => f._id === file._id);
  };

  useEffect(() => {
    if (Array.isArray(selectedData)) {
      const preSelected =
        list?.data?.filter((file) => selectedData.includes(file._id)) || [];
      setSelectedItems((prev) => {
        // Merge without duplicates (_id-based)
        const combined = [...prev];
        for (const item of preSelected) {
          if (!combined.some((f) => f._id === item._id)) {
            combined.push(item);
          }
        }
        return combined;
      });
    }
  }, [selectedData, list]);

  // add or edit media response handle =================>
  function mediaManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      setCurrentPage(null);
      loadList();
      setModalOpen({ isOpen: false, data: null });
      setFileUploadModal({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  function onScroll({ scrollTop, clientHeight, scrollHeight }) {
    if (Math.ceil(scrollTop) >= scrollHeight - clientHeight && pagination) {
      setCurrentPage(pagination);
    }
  }

  return (
    <div className="p-2 space-y-5">
      <Breadcrumb items={breadcrumbList} onNavigate={handleNavigate} />

      <div className="flex justify-between items-center mb-4 flex-col xls:flex-row flex-wrap gap-4">
        <h2 className="text-xl font-semibold flex-shrink-0">Folders & Files</h2>

        <div className="flex flex-wrap items-center justify-center xls:justify-end gap-3 w-full xls:w-auto">
          <div className="w-full sm:w-64 md:w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                setCurrentPage(null);
              }}
            />
          </div>

          <div className="w-full sm:w-32 md:w-36">
            <Select
              dropdownClass={"!w-44"}
              labelClasses={"!w-full"}
              onChange={(e) => {
                setType(e);
                setCurrentPage(null);
              }}
              selectedValue={type}
              dropdownData={optionData?.map((cn) => ({
                name: cn?.label,
                value: cn?.value,
              }))}
            />
          </div>

          <div className="flex items-center justify-center md:justify-end gap-2 w-full sm:w-auto">
            <Button
              buttonClasses={"hover:bg-emerald-700"}
              buttonLabel={"Create Folders"}
              buttonIcon={"fa-regular fa-plus"}
              buttonIconPosition="left"
              buttonLabelClasses={"font-medium !text-sm"}
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
            <Button
              buttonClasses={"hover:bg-emerald-700"}
              buttonLabel={"Upload File"}
              buttonIcon={"fa-regular fa-plus"}
              buttonIconPosition="left"
              buttonLabelClasses={"font-medium !text-sm"}
              buttonFunction={() =>
                setFileUploadModal({ isOpen: true, data: null })
              }
            />
          </div>
        </div>
      </div>
      <div
        className={classNames(
          "overflow-auto py-2 scroll-smooth h-[calc(60vh-80px)] w-full xls:pr-3"
        )}
        onScroll={(e) => {
          onScroll({
            scrollTop: e.target.scrollTop,
            clientHeight: e.target.clientHeight,
            scrollHeight: e.target.scrollHeight,
          });
        }}
      >
        <div className="grid grid-cols-2 lg:grid-cols-5 xls:grid-cols-8 gap-4 w-full">
          {list?.data?.map((file, index) => (
            <MediaCard
              key={index}
              file={file}
              isSelected={isSelected}
              toggleSelectedItem={toggleSelectedItem}
              onFolderClick={(folder) => {
                if (folder?.uid && folder?.name) {
                  handleNavigate({ uid: folder?.uid, name: folder?.name });
                }
              }}
              selectMedia={true}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-end items-end relative ml-auto">
        <Button
          buttonClasses={"hover:bg-emerald-700"}
          buttonLabel={"Select Now"}
          buttonIcon={"fa-regular fa-plus"}
          buttonIconPosition="left"
          buttonLabelClasses={"font-medium !text-sm"}
          buttonFunction={() => {
            onSelect(selectedItems);
            close();
          }}
        />
      </div>

      <ManageFolder
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={mediaManageResponse}
      />
      <UploadFile
        open={fileUploadModal?.isOpen}
        data={fileUploadModal?.data}
        close={() => setFileUploadModal({ isOpen: false, data: null })}
        getResponse={mediaManageResponse}
      />
    </div>
  );
}
